import { Spinner, Tooltip } from "@shopify/polaris"
import React, { useContext, useEffect, useState } from "react"
import { useCheckScopes } from "../hooks/useProtectHooks.js"
import { CoolButton } from "./CoolButton"
import { GlobalStateContext } from "./providers/GlobalStateProvider.jsx"
import { useTranslation } from "react-i18next"
import { Video } from "./Video.jsx"

export const ScopesUpdateTutorial = ({
	hasNeccessaryScopes,
	onRequiredScopesChecked,
	requiredScopes,
	context,
	videoSrc,
}) => {
	const { t } = useTranslation()
	const { state, dispatch } = useContext(GlobalStateContext)
	const checkScopes = useCheckScopes()

	const [loadingFirst, setLoadingFirst] = useState(false)
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [enabledScopes, setEnabledScopes] = useState([])

	const openEditPermissions = () => {
		const scopesUrl = `https://admin.shopify.com/store/${state.shop.split(".myshopify.com")?.[0]}/settings/apps/development/${state.appId}/configuration/admin_api_integration`
		window.open(scopesUrl, "_blank")
	}

	const _checkScopes = async (firstLoad = false) => {
		if (firstLoad) {
			setLoadingFirst(true)
		}

		setLoading(true)
		const { hasNeccessaryScopes, enabledScopes } =
			await checkScopes(requiredScopes)
		setLoading(false)

		setEnabledScopes(enabledScopes)

		if (enabledScopes.length) {
			dispatch({ currentScopes: enabledScopes })
		}

		if (firstLoad) {
			setLoadingFirst(false)
		}

		if (hasNeccessaryScopes) {
			onRequiredScopesChecked()
			window.removeEventListener("focus", _checkScopes)
		}
	}

	useEffect(() => {
		_checkScopes(true)

		// Add event listener on tab change
		window.addEventListener("focus", _checkScopes)

		return () => {
			window.removeEventListener("focus", _checkScopes)
		}
	}, [])

	if (loadingFirst) {
		return (
			<div
				style={{
					width: "fit-content",
					marginLeft: "auto",
					marginRight: "auto",
				}}
			>
				<Spinner size="large" />
			</div>
		)
	}

	return (
		<div
			style={{
				maxWidth: "500px",
				marginLeft: "auto",
				marginRight: "auto",
			}}
		>
			<h1
				style={{
					fontSize: "2rem",
					fontWeight: "800",
					marginBottom: "1rem",
				}}
			>
				{t(
					"components.scopes_update_tutorial.additional_scopes_required",
				)}
			</h1>

			<div
				style={{
					justifyContent: "center",
					gap: "0.5rem",
					background: "#222",
					borderRadius: "1rem",
					padding: "1rem",
				}}
			>
				{context === "pages" && (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "0.5rem",
						}}
					>
						<svg
							style={{
								width: "1.3rem",
								height: "1.3rem",
								color: "#fff",
							}}
							viewBox="0 0 24 24"
						>
							<path
								fill="currentColor"
								d="M16.08 6.63c-.07-.1-.15-.19-.23-.27h-.01a2.85 2.85 0 0 0-1.11-.69l-1.38-.45c-.1-.04-.2-.11-.26-.2a.5.5 0 0 1-.1-.31a.5.5 0 0 1 .1-.31c.06-.09.15-.16.26-.2l1.38-.45c.42-.14.79-.38 1.1-.69c.29-.3.52-.67.67-1.07v-.03l.45-1.38c.04-.1.11-.2.2-.26a.5.5 0 0 1 .31-.1c.11 0 .22.04.31.1s.16.15.2.26l.45 1.38c.14.42.38.8.69 1.11c.31.32.69.55 1.11.69l1.38.45h.03c.1.04.19.11.26.2s.1.2.1.31a.5.5 0 0 1-.1.31c-.06.09-.15.16-.26.2l-1.38.45c-.42.14-.8.38-1.11.69c-.32.31-.55.69-.69 1.11l-.46 1.38v.03c-.04.09-.11.17-.19.23a.5.5 0 0 1-.31.1a.5.5 0 0 1-.31-.1a.52.52 0 0 1-.2-.26l-.45-1.38c-.1-.31-.25-.6-.45-.85m7.7 3.8l-.77-.24c-.24-.08-.45-.21-.62-.38c-.17-.18-.3-.39-.38-.62l-.25-.76a.33.33 0 0 0-.11-.15a.27.27 0 0 0-.34 0c-.05.04-.09.09-.11.15l-.25.76c-.07.23-.2.44-.37.61s-.38.3-.61.38l-.77.25c-.06.02-.11.06-.15.11a.27.27 0 0 0 0 .34c.04.05.09.09.15.11l.77.25c.23.07.45.21.62.38s.3.39.38.62l.25.76c.02.06.06.11.11.15a.27.27 0 0 0 .34 0c.05-.04.09-.09.11-.15l.25-.76c.08-.24.21-.45.38-.62c.18-.17.39-.3.62-.38l.77-.25c.06-.02.11-.06.15-.11s.05-.11.05-.17s-.01-.12-.05-.17s-.106-.08-.17-.11m-4.24 2.28l.26.8c.05.14.12.25.2.37v6.08c0 1.24-1.01 2.25-2.25 2.25H6.25C5.01 22.21 4 21.2 4 19.96V4.46c0-1.24 1.01-2.25 2.25-2.25h8.26c-.07.04-.15.08-.22.11l-1.42.46c-.4.15-.75.4-.99.75a2 2 0 0 0-.38 1.18c0 .43.13.84.37 1.18c.078.112.178.202.275.289l.045.041H7.75c-.41 0-.75.34-.75.75s.34.75.75.75h7.24l.035.058a1 1 0 0 1 .085.162l.46 1.41c.14.4.4.74.75.99c.35.24.76.37 1.18.37c0 .37.11.73.33 1.04s.52.53.91.67zm-11.79 5h8.5c.41 0 .75-.34.75-.75s-.34-.75-.75-.75h-8.5c-.41 0-.75.34-.75.75s.34.75.75.75m0-5h8.5c.41 0 .75-.34.75-.75s-.34-.75-.75-.75h-8.5c-.41 0-.75.34-.75.75s.34.75.75.75"
							></path>
						</svg>
						<p
							style={{
								fontSize: "1.25rem",
								fontWeight: "600",
								color: "#fff",
							}}
						>
							{t("kopy_pages.installation")}
						</p>
					</div>
				)}

				<div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
					<div
						style={{
							display: "flex",
							gap: "0.3rem",
							flexDirection: "column",
							marginBottom: "1rem",
							color: "#eee",
						}}
					>
						<div
							style={{
								display: "flex",
								gap: "0.3rem",
								alignItems: "center",
							}}
						>
							<svg
								width="1.5rem"
								height="1.5rem"
								viewBox="0 0 24 24"
							>
								<path
									fill="none"
									stroke="currentColor"
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="2"
									d="M4 7.2v9.6c0 1.12 0 1.68.218 2.108a2 2 0 0 0 .874.874c.427.218.987.218 2.105.218h9.606c1.118 0 1.677 0 2.104-.218c.377-.192.683-.498.875-.874c.218-.428.218-.986.218-2.104V7.197c0-1.118 0-1.678-.218-2.105a2.001 2.001 0 0 0-.875-.874C18.48 4 17.92 4 16.8 4H7.2c-1.12 0-1.68 0-2.108.218a1.999 1.999 0 0 0-.874.874C4 5.52 4 6.08 4 7.2"
								></path>
							</svg>
							<p style={{ fontSize: "1rem" }}>
								{t("kopy_pages.step_1")}
							</p>
							<Tooltip
								content={
									<div
										style={{
											width: "35rem",
											padding: "0.5rem",
										}}
									>
										<Video
											isInsideTooltip={true}
											videoSrc={videoSrc}
										/>
									</div>
								}
							>
								<div>
									<svg
										width="1rem"
										height="1rem"
										viewBox="0 0 24 24"
									>
										<path
											fill="#fff"
											d="M11.95 18q.525 0 .888-.363t.362-.887q0-.525-.362-.888t-.888-.362q-.525 0-.887.363t-.363.887q0 .525.363.888t.887.362m-.9-3.85h1.85q0-.825.188-1.3t1.062-1.3q.65-.65 1.025-1.238T15.55 8.9q0-1.4-1.025-2.15T12.1 6q-1.425 0-2.312.75T8.55 8.55l1.65.65q.125-.45.563-.975T12.1 7.7q.8 0 1.2.438t.4.962q0 .5-.3.938t-.75.812q-1.1.975-1.35 1.475t-.25 1.825M12 22q-2.075 0-3.9-.787t-3.175-2.138q-1.35-1.35-2.137-3.175T2 12q0-2.075.788-3.9t2.137-3.175q1.35-1.35 3.175-2.137T12 2q2.075 0 3.9.788t3.175 2.137q1.35 1.35 2.138 3.175T22 12q0 2.075-.788 3.9t-2.137 3.175q-1.35 1.35-3.175 2.138T12 22m0-2q3.35 0 5.675-2.325T20 12q0-3.35-2.325-5.675T12 4Q8.65 4 6.325 6.325T4 12q0 3.35 2.325 5.675T12 20m0-8"
										></path>
									</svg>
								</div>
							</Tooltip>
						</div>

						<ul style={{ paddingLeft: "3rem", margin: "0rem" }}>
							{requiredScopes.map((scope, index) => {
								if (enabledScopes.includes(scope)) {
									return null
								}

								return (
									<li key={index}>
										<b
											style={{
												textDecoration:
													"underline dotted",
											}}
										>
											{scope}
										</b>
									</li>
								)
							})}
						</ul>
					</div>
				</div>

				<CoolButton onClick={loading ? null : openEditPermissions}>
					{loading ? (
						<Spinner size="small" />
					) : (
						<span>{t("kopy_pages.enable_permissions")}</span>
					)}
				</CoolButton>
			</div>
		</div>
	)
}
